import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, Home, Sparkles, CalendarCheck, KeyRound, Truck,
  Building, Briefcase, Building2, ChefHat, Bath, Feather, Layers, HardHat,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Stagger, StaggerItem, LineReveal } from "@/components/Reveal";
import { BUSINESS, SERVICES } from "@/data/site";

const ICONS = { Home, Sparkles, CalendarCheck, KeyRound, Truck, Building, Briefcase, Building2, ChefHat, Bath, Feather, Layers, HardHat };

export default function ServicesPage() {
  return (
    <>
      <SEO
        title="Cleaning Services | Julisa's House Cleaning — Kansas City Metro"
        description="Residential, deep, recurring, move-in/move-out, apartment, office and commercial cleaning services in Overland Park, Lenexa, Olathe and Kansas City. Request a free quote."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-white pt-14 pb-12 sm:pt-20 sm:pb-16">
        <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-sky-100/70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
            Our Services
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            <LineReveal lines={["Cleaning services for", <>every space <span className="text-sky-600 italic">you care about</span></>]} />
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Whether it's your family home in Overland Park, an apartment in Kansas City, MO, or an
            office anywhere in the metro, every service below is delivered with the same
            responsibility, punctuality, and attention to detail.
          </p>
        </div>
      </section>

      <section className="bg-sky-50/70 pt-14 pb-20 sm:pb-28" aria-label="List of cleaning services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <StaggerItem key={s.slug}>
                  <article
                    data-testid={`service-card-${s.slug}`}
                    className="group relative h-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl"
                  >
                    <span className="absolute right-6 top-6 font-display text-3xl text-sky-100 transition-colors duration-300 group-hover:text-sky-200" aria-hidden="true">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-600 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <h2 className="mt-5 text-xl font-bold text-slate-900">{s.title}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.blurb}</p>
                    <Link
                      to="/quote"
                      data-testid={`service-quote-link-${s.slug}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                    >
                      Request a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </article>
                </StaggerItem>
              );
            })}
          </Stagger>

          <Reveal className="mt-14">
            <div className="relative overflow-hidden rounded-3xl bg-sky-600 px-6 py-12 sm:px-12 text-center">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
                Not sure which service fits? Let's talk it through.
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-sky-100">
                Tell us about your space and we'll recommend the right cleaning for it — free, and with no pressure.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
                <Link
                  to="/quote"
                  data-testid="services-cta-quote-button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-semibold text-sky-700 shadow-lg transition-all duration-200 hover:bg-sky-50 active:scale-[0.98]"
                >
                  Request a Free Quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="services-cta-call-button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-all duration-200 hover:bg-white/20 active:scale-[0.98]"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" /> {BUSINESS.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
